import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { publicApi } from '@/utils/api';
import { Sparkles, Send, CheckCircle2, ArrowLeft, Zap } from 'lucide-react';

export default function FreeAudit() {
  usePageMeta({
    title: 'Free automation audit · 15-min strategy call',
    description: "Tell us your #1 manual process. We'll send a custom automation plan within 1 business day — completely free.",
  });

  const [form, setForm] = useState({
    name: '', email: '', company: '', website: '', main_pain: '', best_time: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { book_url } on success
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await publicApi.freeAudit({ ...form, source_url: window.location.href });
      setDone(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="free-audit-page">
      <header className="px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-black text-sm" style={{ color: 'var(--ink)' }}>
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
          <Zap size={14} />
          <span className="text-xs font-semibold uppercase tracking-wider">Krris OS</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {done ? (
          <div className="card-base p-8 text-center" data-testid="audit-success">
            <CheckCircle2 size={44} className="mx-auto mb-4" style={{ color: '#3C8A5A' }} />
            <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ color: 'var(--ink)' }}>We got it.</h1>
            <p className="text-base mb-6" style={{ color: 'var(--muted)' }}>
              Check your email for confirmation. If you want to skip the wait, grab a 15-min slot now:
            </p>
            <a
              href={done.book_url}
              target="_blank"
              rel="noreferrer"
              className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm"
              data-testid="book-now-btn"
            >
              <Send size={14} /> Book my audit slot
            </a>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider mb-3 px-2 py-1 rounded" style={{ background: '#D9A44120', color: '#8A6B1A' }}>
                <Sparkles size={11} /> Free · No credit card
              </span>
              <h1 className="text-4xl sm:text-5xl font-black mb-3 leading-tight" style={{ color: 'var(--ink)' }}>
                Get a custom automation audit — on the house.
              </h1>
              <p className="text-lg" style={{ color: 'var(--muted)' }}>
                Tell us your #1 manual process. We'll map where the money leaks and email you a tailored plan within 1 business day.
              </p>
            </div>

            <form onSubmit={submit} className="card-base p-6 space-y-4" data-testid="audit-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Your name *</label>
                  <input className="input-agency text-sm" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="audit-name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Email *</label>
                  <input className="input-agency text-sm" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="audit-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Company</label>
                  <input className="input-agency text-sm" value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Website</label>
                  <input className="input-agency text-sm" placeholder="https://" value={form.website} onChange={e => setForm({...form, website: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>The #1 process you'd automate first *</label>
                <textarea
                  className="input-agency text-sm resize-none"
                  rows={4}
                  required
                  placeholder="e.g. Every week I manually copy orders from our Shopify store into our accounting system…"
                  value={form.main_pain}
                  onChange={e => setForm({...form, main_pain: e.target.value})}
                  data-testid="audit-pain"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Best time to reach you</label>
                <input className="input-agency text-sm" placeholder="Mornings CET, afternoons PST…" value={form.best_time} onChange={e => setForm({...form, best_time: e.target.value})} />
              </div>

              {error && <p className="text-sm p-2 rounded-btn" style={{ background: '#FCE9E5', color: '#B34A3A' }}>{error}</p>}

              <button
                type="submit"
                disabled={loading || !form.email || !form.name || !form.main_pain}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3"
                data-testid="audit-submit"
              >
                <Send size={14} /> {loading ? 'Sending…' : 'Get my free audit'}
              </button>

              <p className="text-xs text-center" style={{ color: 'var(--muted)' }}>
                Zero spam. Zero sales pitch. We reply within 1 business day or you pick a slot directly.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
